import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useState } from "react";
import { LayoutGrid } from "lucide-react";
import { G1_APPS } from "./apps.js";
/**
 * Runtime cross-app registry. The list of apps + their URLs + lifecycle status
 * comes from a server endpoint (auth.g1.network/apps-registry, sourced from
 * network-catalog DOMAINS) so adding/retiring an app is ONE server deploy — no
 * republishing @theg1group/ui across every SPA. Icons are React components and
 * can't be JSON, so they're merged in from the bundled G1_APPS by id (unknown
 * ids get a default icon + prettified name). On any fetch failure the bundled
 * G1_APPS is the offline fallback.
 */
const DEFAULT_ENDPOINT = "https://auth.g1.network/apps-registry";
const AppsCtx = createContext(null);
/**
 * The cross-app list. Returns the runtime registry when an
 * `<AppsRegistryProvider>` is mounted and has loaded; otherwise the bundled
 * `G1_APPS` (also the offline fallback). Consumers: AppSwitcher, UniversalLauncher.
 */
export function useApps() {
    return useContext(AppsCtx) ?? G1_APPS;
}
function prettify(id) {
    return id.charAt(0).toUpperCase() + id.slice(1);
}
function mergeWithBundled(data) {
    const byId = new Map(G1_APPS.map((a) => [a.id, a]));
    return data.map((r) => {
        const base = byId.get(r.id);
        return {
            id: r.id,
            name: r.name ?? base?.name ?? prettify(r.id),
            url: r.url ?? base?.url ?? `https://${r.id}.g1.network`,
            // Icons aren't serializable — take the bundled component by id, else default.
            Icon: base?.Icon ?? LayoutGrid,
            zone: base?.zone,
            purpose: r.purpose ?? base?.purpose,
        };
    });
}
export function AppsRegistryProvider({ endpoint = DEFAULT_ENDPOINT, source = "runtime", children, }) {
    const [apps, setApps] = useState(G1_APPS);
    useEffect(() => {
        if (source === "static")
            return;
        let cancelled = false;
        fetch(endpoint, { credentials: "include" })
            .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`apps-registry ${r.status}`))))
            .then((body) => {
            if (!cancelled && Array.isArray(body.data) && body.data.length > 0) {
                setApps(mergeWithBundled(body.data));
            }
        })
            .catch(() => {
            // keep the bundled G1_APPS fallback
        });
        return () => {
            cancelled = true;
        };
    }, [endpoint, source]);
    return _jsx(AppsCtx.Provider, { value: apps, children: children });
}
//# sourceMappingURL=apps-registry.js.map